import React, { useEffect, useState } from "react";
import { Alert, Button, Col, FormCheck, Modal, Row } from "react-bootstrap";
import { Invite } from "../../models/invite.model";
import { USER_AUTH_TYPE } from "../../enums/user-auth-type.enum";
import { getAuthTypeLabel } from "../../utils/auth-type-label";
import LocationList from "../LocationList";
import { selectUserOwner } from "../../store/user-owner/user-owner.selector";
import { useAppSelector } from "../../store";
import { createAxiosInstance } from "../../utils/axios-instance";
import { msalConfig } from "../../config/msalConfig";
import { ProgramType } from "../../models/program-type.model";
import LocationListModal from "../LocationListModal";
import { BASELINE_REWARD_SETTINGS_ENUM } from "../../enums/baseline-reward-settings.enum";
import { formatDateWithTimezone } from '../../utils/get-date';
import { createMsalInstance } from "../../utils/create-msal-instance";
type VerifyInvitationModalProps = {
  invite: Partial<Invite> | null;
  show: boolean;
  handleClose: () => void;
  confirmSubmitInvitation: (invitationLink: string) => void;
  userAuthType: USER_AUTH_TYPE;
  programType: ProgramType;
  isUpdateInvitation?: boolean;
  inviteEmailLink?: string;
};

export default function VerifyInvitationModal({
  invite,
  show,
  handleClose,
  confirmSubmitInvitation,
  userAuthType,
  programType,
  isUpdateInvitation,
  inviteEmailLink,
}: VerifyInvitationModalProps) {
  const [isAcknowledge, setIsAcknowledge] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const owner = useAppSelector(selectUserOwner);
  const [enrollmentError, setEnrollmentError] = useState("");

  const onSubmit = async () => {
    setIsProcessing(true);
    setEnrollmentError("");
    try {
      const authority = `${process.env.REACT_APP_MSAL_AUTHORITY}/${USER_AUTH_TYPE.COMARCH_EMPLOYEE}`;
      msalConfig.auth.authority = authority;
      const msalInstance = await createMsalInstance();
      const axiosInstance = createAxiosInstance(msalInstance);
      const httpMethod = isUpdateInvitation ? "patch" : "post";
      let payload;
      if (isUpdateInvitation) {
        const parts = (inviteEmailLink as string).split("token=");
        payload = {
          invitationKey: parts[1],
          enrolled: 0,
          enrolledDate: "",
          termsAndConditionsAccepted: 0,
          acceptedDateDate: "",
          ownerFirstName: invite?.ownerFirstName,
          ownerLastName: invite?.ownerLastName,
          ownerEmail: invite?.ownerEmail,
          programType,
          loyaltyId: `${programType}-${invite?.ownerID}`,
          baselineRewardSetting: invite?.baselineRewardSetting,
          inviteUpdFlag: true,
        };
      } else {
        payload = {
          ...owner,
          ownerFirstName: invite?.ownerFirstName,
          ownerLastName: invite?.ownerLastName,
          ownerEmail: invite?.ownerEmail,
          programType,
          loyaltyId: `${programType}-${invite?.ownerID}`,
          invitedDate: formatDateWithTimezone(new Date()),
          baselineRewardSetting: invite?.baselineRewardSetting,
        };
      }
      //console.log('@@ - INVITE payload => ', JSON.stringify(payload));
      const res = await axiosInstance[httpMethod](
        `${process.env.REACT_APP_INVITE_API_URL}/rewards/enrollment`,
        payload
      );
      const invitationLink = isUpdateInvitation
        ? res.data.invitationKey
        : (res.data.invitationLink as string);
      localStorage.removeItem("ownerId");
      localStorage.removeItem("employeeId");
      confirmSubmitInvitation(invitationLink);
    } catch (err) {
      let error = "";
      if (typeof err === "object" && err !== null && "response" in err) {
        const response = (err as any).response;
        error = response?.data?.message;
      }
      if (!error.includes('Sorry, you are inviting a user who already exist for a different owner')) {
        setIsProcessing(false);
      }
      setEnrollmentError(error || "We had a problem trying to process the submission. Please reach out to customer service for assistance.");
    }
  };

  const onCloseModal = () => {
    setEnrollmentError("");
    handleClose();
  }
  const getProgramTypeLabel = () => {
    switch (programType) {
      case 'PB':
        return 'PracticeBuilder';
      case 'PRP':
        return 'Partner Rewards Hub';
      default:
        return 'Rewards Program'; // fallback
    }
  };

  const renderErrorWithEmailLinks = (text: string) => {
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const parts = text.split(emailRegex);

    return parts.map((part, index) => {
      if (emailRegex.test(part)) {
        return (
          <a
            key={index}
            href={`mailto:${part}`}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Reset processing state when modal is closed
  useEffect(() => {
    if (!show) {
      setIsProcessing(false);
      setIsAcknowledge(false);
    }
  }, [show]);

  return (
    <Modal size="lg" show={show} onHide={onCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>Verify {getAuthTypeLabel(userAuthType)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LocationListModal />
        <p className="mt-2 text-right">
          <span className="text-red-600">Note</span> - Lab account(s) and
          Location(s) information will be auto-updated in future, if we identify
          more linked location(s) and account(s) to your practice.
        </p>
        <div className="flex flex-col gap-2 mt-4">
          {/*<div>*/}
          {/*  <h3 className="font-bold">Owner Id:</h3>*/}
          {/*  <h2>{invite?.ownerID}</h2>*/}
          {/*</div>*/}
          <div>
            <h3 className="font-bold">First Name:</h3>
            <h2>{invite?.ownerFirstName}</h2>
          </div>
          <div>
            <h3 className="font-bold">Last Name:</h3>
            <h2>{invite?.ownerLastName}</h2>
          </div>
          <div>
            <h3 className="font-bold">Email:</h3>
            <h2>{invite?.ownerEmail}</h2>
          </div>
          <div>
            <h3 className="font-bold">Baseline Reward Setting:</h3>
            <h2>
              {invite?.baselineRewardSetting === "OWNER"
                ? BASELINE_REWARD_SETTINGS_ENUM.OWNER
                : BASELINE_REWARD_SETTINGS_ENUM.LOCATION}
            </h2>
          </div>
        </div>
        <FormCheck
          onChange={() => setIsAcknowledge(!isAcknowledge)}
          className="mt-4"
          inline
          label={`Acknowledge ${getAuthTypeLabel(
            userAuthType
          ).toLowerCase()} to the ECP above for invite to ${getProgramTypeLabel()} program`}
          type="checkbox"
        />
        {enrollmentError && (
          <Alert className="text-center mx-auto mt-4" variant="danger">
            {renderErrorWithEmailLinks(enrollmentError)}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-between w-full">
          <Button
            variant="secondary"
            onClick={onCloseModal}
            disabled={isProcessing && !enrollmentError}
          >
            Cancel
          </Button>
          <Button
            id="submitButton"
            onClick={onSubmit}
            disabled={!isAcknowledge || isProcessing}
            variant="primary"
          >
            {(isProcessing && !enrollmentError)
              ? "Processing..."
              : isUpdateInvitation
              ? "Confirm Update Invitation"
              : `Confirm Submit ${getAuthTypeLabel(userAuthType)}`}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
